import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Row, Col, Form } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import { selectToken } from "../auth/auth.selector";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import "../../styles/ticket-system/reopen-ticket-modal.css";

const ReopenTicketModal = ({
  isOpen,
  onClose,
  ticket,
  onSubmit,
  userInfoData,
  handleReload,
}) => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const { supervisors, supervisorsLoading } = useSelector(
    (state) => state.ticketSystem
  );

  // Form state
  const [reopeningRemarks, setReopeningRemarks] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Validation function
  const validateForm = () => {
    const errors = {};

    if (
      !reopeningRemarks ||
      reopeningRemarks.replace(/<[^>]*>/g, "").trim().length < 10
    ) {
      errors.reopeningRemarks =
        "Reopening remarks must be at least 10 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ticket_id: ticket?.uid,
        reopening_remarks: reopeningRemarks.replace(/<[^>]*>/g, "").trim(),
        re_opened_by: userInfoData?.uid || userInfoData?.user_id,
      };

      // Add assigned_to if supervisor is selected
      if (assignedTo) {
        payload.assigned_to = assignedTo;
      }

      const result = await dispatch(
        fetchApiData({
          method: "POST",
          endpoint: "/ticketSystem/reopen",
          payload: payload,
          token: token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          result.payload.message || "Ticket reopened successfully!"
        );
        resetForm();
        onClose();
        if (handleReload) {
          handleReload();
        }
        if (onSubmit) {
          onSubmit();
        }
      } else {
        toast.error(result?.payload?.message || "Failed to reopen ticket");
      }
    } catch (error) {
      console.error("Error reopening ticket:", error);
      toast.error("An error occurred while reopening ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setReopeningRemarks("");
    setAssignedTo("");
    setFormErrors({});
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Reopen Ticket"
      ariaHideApp={false}
      closeTimeoutMS={300}
    >
      <div className="reopen-ticket-container">
        <div style={{ padding: "10px" }}>
          <ModalHeadAndClose
            title={`Reopen Ticket - ${ticket?.ticket_no || ""}`}
            subtitle={`${ticket?.gym_name || "WTF Gym"} - ${
              ticket?.issue_type_name || ""
            } - ${ticket?.category_name || ""}`}
            onClose={handleClose}
          />
        </div>

        <div className="modal-body-content">
          {/* Reopening Remarks */}
          <div className="form-section">
            <label className="section-label required">REOPENING REMARKS</label>
            <div
              className={`reopen-textarea-wrapper ${
                formErrors.reopeningRemarks ? "error" : ""
              }`}
            >
              <ReactQuill
                value={reopeningRemarks}
                onChange={setReopeningRemarks}
                placeholder="Explain why this ticket needs to be reopened, what issues were found, or what additional work is required..."
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["clean"],
                  ],
                }}
              />
              <div className="char-count">
                {reopeningRemarks.replace(/<[^>]*>/g, "").length} characters
              </div>
            </div>
            {formErrors.reopeningRemarks && (
              <span className="error-message">
                {formErrors.reopeningRemarks}
              </span>
            )}
          </div>

          {userInfoData?.ticket_account_type === "admin" &&
            userInfoData?.ticket_account_type === "manager" && (
              <>
                {/* Optional Assignment */}
                <div className="form-section">
                  <label className="section-label">
                    REASSIGN TO SUPERVISOR (Optional)
                  </label>
                  <Form.Select
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    disabled={supervisorsLoading}
                    className="supervisor-select"
                  >
                    <option value="">Select Supervisor (Optional)</option>
                    {supervisors.map((supervisor) => (
                      <option key={supervisor.value} value={supervisor.value}>
                        {supervisor.label}
                      </option>
                    ))}
                  </Form.Select>
                  <small className="form-text">
                    If no supervisor is selected, the ticket will be reopened
                    without assignment
                  </small>
                </div>
              </>
            )}

          {/* Action Buttons */}
          <div className="modal-actions">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="reopen-btn"
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Reopening...
                </>
              ) : (
                "Reopen Ticket"
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReopenTicketModal;
